// Usage:
// <div data-controller="widget-configurator"
//      data-widget-configurator-script-url-value="https://example.com/get-stoked-modal.js"
//      data-widget-configurator-widget-type-value="modal">

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static values = {
    scriptUrl: String,
    widgetType: String, // "modal" or "map"
    mapUlid: String // set while editing a saved map; the embed code then references it by ID
  }

  static targets = ["codeOutput", "preview", "copyButton", "visitorFilters"]

  connect() {
    this.resetFieldsToDefaults()
    this.updateCode()
  }

  // Undo browser form-state restoration on hard refresh. The configurator is a
  // code generator, not a form to submit — values should always start from the
  // server-rendered defaults rather than the visitor's last typed values.
  resetFieldsToDefaults() {
    this.element.querySelectorAll("[data-field]").forEach((field) => {
      if (field.tagName === "INPUT") {
        if (field.type === "checkbox" || field.type === "radio") {
          field.checked = field.defaultChecked
        } else {
          field.value = field.defaultValue
        }
      } else if (field.tagName === "FIELDSET") {
        // HotwireCombobox: the canonical value lives in the hidden input,
        // and the visible search input mirrors the selected option's label.
        field.querySelectorAll("input").forEach((input) => {
          if (input.type === "checkbox" || input.type === "radio") {
            input.checked = input.defaultChecked
          } else {
            input.value = input.defaultValue
          }
        })
      } else if (field.tagName === "SELECT") {
        Array.from(field.options).forEach((opt) => {
          opt.selected = opt.defaultSelected
        })
      }
    })
  }

  // Enter in a configuration field should only refresh the preview, never save
  // the map; only the map name field submits, and textareas keep their newlines.
  preventImplicitSubmit(event) {
    if (event.target.name !== "name" && event.target.tagName !== "TEXTAREA") event.preventDefault()
  }

  updateCode() {
    const code = this.buildCode()
    if (this.hasVisitorFiltersTarget) this.visitorFiltersTarget.hidden = this.fieldValue("unlock-filters") !== true
    // A new, unsaved map has no embed code panel yet; it still gets a preview.
    if (this.hasCodeOutputTarget) this.codeOutputTarget.textContent = code
    if (this.hasCopyButtonTarget) this.copyButtonTarget.setAttribute("data-clipboard-text", code)
    // A saved map's code is just its ID; preview the unsaved field values instead.
    const previewCode = this.mapUlidValue ? this.buildMapCode({ inline: true }) : code
    // The preview iframe gets the same snippet plus an internal marker that
    // tells the embedded landing controller to force-center on the
    // server-computed map_center and skip writing to localStorage. Without
    // this, a previous preview's saved center would shadow the current
    // settings. The marker is not in the visible / copy-pasteable code.
    this.updatePreview(previewCode.replace(/<script /, '<script data-stoked-preview="true" '))
  }

  buildCode() {
    if (this.widgetTypeValue === "modal") {
      return this.buildModalCode()
    } else {
      return this.buildMapCode()
    }
  }

  buildModalCode() {
    const attrs = [`src="${this.scriptUrlValue}"`, "defer"]

    this.addAttr(attrs, "position", "data-position", "lower-left")
    this.addAttr(attrs, "button-text", "data-button-text", "", { escape: true })
    this.addAttr(attrs, "button-color", "data-button-color", "#e53b2a")
    this.addAttr(attrs, "button-text-color", "data-button-text-color", "#ffffff")
    this.addAttr(attrs, "delay", "data-button-delay", "0")
    this.addAttr(attrs, "offset-x", "data-offset-x", "30")
    this.addAttr(attrs, "offset-y", "data-offset-y", "30")
    this.addAttr(attrs, "location", "data-location", "", { escape: true })
    this.addAttr(attrs, "zoom", "data-zoom", "")

    return `<script ${attrs.join(" ")}></script>`
  }

  buildMapCode({ inline = false } = {}) {
    const attrs = [`src="${this.scriptUrlValue}"`, "defer"]

    this.addAttr(attrs, "width", "data-width", "100%", { escape: true })
    this.addAttr(attrs, "height", "data-height", "600px", { escape: true })
    this.addAttr(attrs, "border", "data-border", "0", { escape: true })
    this.addAttr(attrs, "title", "data-title", "Community Map", { escape: true })

    if (this.fieldValue("full-bleed") === true) {
      attrs.push(`data-full-bleed="true"`)
    }

    if (this.mapUlidValue && !inline) {
      attrs.push(`data-map="${this.mapUlidValue}"`)
      return `<script ${attrs.join(" ")}></script>`
    }

    this.addAttr(attrs, "location", "data-location", "", { escape: true })
    this.addAttr(attrs, "zoom", "data-zoom", "")
    // Always emitted: an embed without data-map otherwise falls back to its own defaults.
    this.addAttr(attrs, "pins", "data-pins", "")
    this.addAttr(attrs, "pin-size", "data-pin-size", "")
    this.addAttr(attrs, "unmatched-pins", "data-unmatched-pins", "")
    this.addAttr(attrs, "unmatched-pin-size", "data-unmatched-pin-size", "")
    this.addAttr(attrs, "unmatched-cards", "data-unmatched-cards", "")
    this.addAttr(attrs, "no-match-fallback", "data-no-match-fallback", "")
    // The branding color stays unset so the embed keeps following Settings → Branding.
    const brandingColor = this.element.querySelector('[data-field="unmatched-pin-color"]')?.dataset.brandingColor || ""
    this.addAttr(attrs, "unmatched-pin-color", "data-unmatched-pin-color", brandingColor)

    const filters = this.collectFilters()
    if (filters) {
      attrs.push(`data-filters='${this.escapeHtml(filters)}'`)
    }

    if (this.fieldValue("unlock-filters") === true) {
      attrs.push(`data-lock-filters="false"`)

      const filterSets = this.collectVisibleFilterSets()
      if (filterSets) attrs.push(`data-filter-sets="${filterSets}"`)
    }

    return `<script ${attrs.join(" ")}></script>`
  }

  addAttr(attrs, fieldName, dataAttr, defaultValue, { escape = false } = {}) {
    const value = this.fieldValue(fieldName)
    if (value && value !== defaultValue) {
      const safe = escape ? this.escapeHtml(value) : value
      attrs.push(`${dataAttr}="${safe}"`)
    }
  }

  collectFilters() {
    const out = {}
    this.element.querySelectorAll("[data-filter-set-ulid]").forEach((el) => {
      const setUlid = el.dataset.filterSetUlid
      const value = this.fieldValue(`filter-${setUlid}`)
      if (value) out[setUlid] = value
    })
    return Object.keys(out).length ? JSON.stringify(out) : null
  }

  // All checked means "every enabled set", so the attribute is omitted.
  collectVisibleFilterSets() {
    const boxes = Array.from(this.element.querySelectorAll("[data-visible-filter-set]"))
    const checked = boxes.filter((box) => box.checked).map((box) => box.dataset.visibleFilterSet)
    return checked.length && checked.length < boxes.length ? checked.join(",") : null
  }

  fieldValue(name) {
    const field = this.element.querySelector(`[data-field="${name}"]`)
    if (!field) return null

    if (field.tagName === "INPUT" && (field.type === "checkbox" || field.type === "radio")) {
      return field.checked
    }

    // HotwireCombobox places data attrs on the fieldset; the actual value
    // lives in the hidden input inside it.
    if (field.tagName === "FIELDSET") {
      const hidden = field.querySelector("input[type='hidden']")
      return hidden ? hidden.value : null
    }

    return field.value
  }

  updatePreview(code) {
    if (!this.hasPreviewTarget) return

    const html = `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"></head>
<body style="margin:0;padding:0;">
${code}
</body>
</html>`
    this.previewTarget.srcdoc = html
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
